<div class="transaction-batch-container">

    <!-- Loading the batch itself. The transactions request is only issued once the batch
         resolves, so a bad id costs one request, not two. -->
    <div class="page-loading" *ngIf="isBatchLoading">
        <mat-spinner diameter="48"></mat-spinner>
    </div>

    <!-- The URL names a batch that is not there (404). Its own state, not an error. -->
    <mat-card class="content-card" *ngIf="!isBatchLoading && batchNotFound">
        <mat-card-content>
            <div class="empty-state">
                <mat-icon>search_off</mat-icon>
                <p>Batch not found</p>
                <span>This batch may have been removed, or the link is incorrect.</span>
                <a mat-stroked-button [routerLink]="listRoute">
                    <mat-icon>arrow_back</mat-icon>
                    Back to Payment Processing
                </a>
            </div>
        </mat-card-content>
    </mat-card>

    <!-- ngIf narrows `batch` to non-null for everything inside. -->
    <ng-container *ngIf="batch">

        <!-- Batch summary card.
             The blocks are the same as a batch card in the list (see transaction-list.component.*),
             so a batch reads the same in both places. What diverges is the dates. The list has room
             only for chips; here the created date joins the batch's other figures on their shared
             value-over-label shape, and each due date gets its own status breakdown on a line of
             its own. There is no heading above it: the summary names the batch, so a generic
             "Batch Details" title would say nothing the summary does not. -->
        <mat-card class="content-card summary-card">
            <mat-card-content>
                <div class="batch-summary">

                    <!-- The way back to the list. First cell of the grid, so it reads as the
                         leading edge of the batch's own identity rather than as page chrome
                         floating somewhere else. -->
                    <button mat-icon-button class="back-btn"
                            [routerLink]="listRoute"
                            matTooltip="Back to Payment Processing"
                            aria-label="Back to Payment Processing">
                        <mat-icon>arrow_back</mat-icon>
                    </button>

                    <!-- Identity: file type, funding type, originator -->
                    <div class="bc-identity">
                        <span class="file-type-badge" [ngClass]="fileTypeBadgeClass(batch.fileType)">
                            {{ batch.fileType || 'EFT' }}
                        </span>
                        <div class="bc-identity-text">
                            <span class="bc-funding">{{ fundingTypeLabel(batch.fundingType) }}</span>
                            <span class="bc-originator" *ngIf="batch.originatorId">{{ batch.originatorId }}</span>
                        </div>
                    </div>

                    <!-- Batch-wide figures: created date, transaction count and total amount, on the
                         same value-over-label shape and in three equal grid columns, with the
                         funding-type block above taking whatever width they leave. The due dates are
                         not among them — they get their own full-width row below, one status
                         breakdown each. -->
                    <div class="bc-stat">
                        <span class="bc-stat-value">{{ batchDate }}</span>
                        <span class="bc-stat-label">Created Date</span>
                    </div>

                    <div class="bc-stat">
                        <span class="bc-stat-value">{{ batch.totalTransaction }}</span>
                        <span class="bc-stat-label">Transactions</span>
                    </div>

                    <div class="bc-stat">
                        <span class="bc-stat-value">{{ FormatUtil.formatCurrency(batch.totalAmountInCents / 100) }}</span>
                        <span class="bc-stat-label">Total Amount</span>
                    </div>

                    <!-- Per-due-date breakdown. A batch may cover several payment dates, and the
                         question an operator has is which of them the unfinished statuses sit on —
                         so each date gets its own breakdown, and doubles as a filter shortcut for
                         the table below. Zero-count statuses are hidden: with no batch-wide
                         breakdown to list the full vocabulary, repeating every status in every card
                         would be noise. -->
                    <div class="bc-due-breakdown">
                        <div class="bdb-head">
                            <span class="bc-fact-label">Due Dates</span>
                            <span class="bdb-hint" *ngIf="dueDateBreakdowns.length">
                                Click a due date to filter the transactions below
                            </span>
                        </div>

                        <div class="bdb-grid" *ngIf="dueDateBreakdowns.length; else noDueDates">
                            <button type="button" class="bdb-card"
                                    *ngFor="let group of dueDateBreakdowns"
                                    [class.is-active]="activeDueDate === group.entry.dueDate"
                                    [attr.aria-pressed]="activeDueDate === group.entry.dueDate"
                                    (click)="selectDueDate(group.entry.dueDate)">
                                <span class="bdb-card-head">
                                    <span class="date-chip">{{ group.entry.dueDate }}</span>
                                    <span class="bdb-amount">
                                        {{ FormatUtil.formatCurrency(group.entry.totalAmountInCents / 100) }}
                                    </span>
                                    <mat-icon class="bdb-active-icon" *ngIf="activeDueDate === group.entry.dueDate">
                                        filter_alt
                                    </mat-icon>
                                </span>
                                <lmc-status-breakdown
                                    [items]="group.items"
                                    [total]="group.entry.totalTransaction"
                                    [showZeros]="false"
                                    totalLabel="Transactions">
                                </lmc-status-breakdown>
                            </button>
                        </div>

                        <ng-template #noDueDates>
                            <span class="bdb-empty">No due dates recorded on this batch's transactions.</span>
                        </ng-template>
                    </div>

                </div>
            </mat-card-content>
        </mat-card>

        <!-- Filters + transactions card -->
        <mat-card class="content-card records-card">
            <mat-card-content>

                <!-- Filters -->
                <form [formGroup]="filterForm" (ngSubmit)="onSearch()" class="batch-filter-form">
                    <div class="filter-grid">

                        <mat-form-field appearance="outline">
                            <mat-label>Due Date From</mat-label>
                            <input matInput [matDatepicker]="dueFromPicker" formControlName="dueDateFrom" placeholder="Select date">
                            <mat-datepicker-toggle matIconSuffix [for]="dueFromPicker"></mat-datepicker-toggle>
                            <mat-datepicker #dueFromPicker></mat-datepicker>
                        </mat-form-field>

                        <!-- Enabled only once Due Date From has a value (the control starts disabled;
                             see syncDueDateTo), and bounded below by it so the range can never invert. -->
                        <mat-form-field appearance="outline">
                            <mat-label>Due Date To</mat-label>
                            <input matInput [matDatepicker]="dueToPicker" [min]="dueDateMin"
                                   formControlName="dueDateTo" placeholder="Select date">
                            <mat-datepicker-toggle matIconSuffix [for]="dueToPicker"></mat-datepicker-toggle>
                            <mat-datepicker #dueToPicker></mat-datepicker>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>Transaction Status</mat-label>
                            <mat-select formControlName="transactionStatus">
                                <mat-option [value]="null">All Statuses</mat-option>
                                <mat-option *ngFor="let status of transactionStatuses" [value]="status">{{ status }}</mat-option>
                            </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>Return Code</mat-label>
                            <mat-select formControlName="returnCode">
                                <mat-option [value]="null">All Return Codes</mat-option>
                                <mat-option *ngFor="let code of returnCodeOptions" [value]="code.value">
                                    {{ code.value }} – {{ code.label }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>Loan #</mat-label>
                            <input matInput formControlName="loanNumber" placeholder="Search by loan number...">
                            <mat-icon matSuffix>search</mat-icon>
                        </mat-form-field>

                        <mat-form-field appearance="outline">
                            <mat-label>Name</mat-label>
                            <input matInput formControlName="name" placeholder="Search by customer name...">
                            <mat-icon matSuffix>search</mat-icon>
                        </mat-form-field>

                    </div>

                    <div class="filter-actions">
                        <div class="action-buttons">
                            <button mat-stroked-button type="button" [disabled]="selectMode !== null" (click)="enterSelectMode('retry')">
                                <mat-icon>refresh</mat-icon>
                                Retry
                            </button>
                            <button mat-stroked-button type="button" [disabled]="selectMode !== null" (click)="enterSelectMode('refund')">
                                <mat-icon>currency_exchange</mat-icon>
                                Refund
                            </button>
                            <button mat-stroked-button type="button" [disabled]="selectMode !== null" (click)="enterSelectMode('export')">
                                <mat-icon>file_download</mat-icon>
                                Export EFT
                            </button>
                            <button mat-stroked-button type="button" [disabled]="selectMode !== null" (click)="enterSelectMode('cancel')">
                                <mat-icon>block</mat-icon>
                                Cancel
                            </button>
                        </div>
                        <div class="query-buttons">
                            <button mat-stroked-button type="button" (click)="onReset()">
                                <mat-icon>refresh</mat-icon>
                                Reset
                            </button>
                            <!-- Also blocked while the form is invalid: `[min]` on Due Date To stops the
                                 calendar offering a date before From, but not one typed straight into
                                 the input, and an inverted range must not reach the backend. -->
                            <button mat-raised-button color="primary" type="submit"
                                    [disabled]="isLoading || filterForm.invalid">
                                <mat-spinner *ngIf="isLoading" diameter="18" class="btn-spinner"></mat-spinner>
                                <mat-icon *ngIf="!isLoading">search</mat-icon>
                                Search
                            </button>
                        </div>
                    </div>
                </form>

                <!-- Loading state -->
                <div class="list-loading" *ngIf="isLoading">
                    <mat-spinner diameter="40"></mat-spinner>
                </div>

                <!-- Empty state -->
                <div class="empty-state" *ngIf="!isLoading && records.length === 0">
                    <mat-icon>inbox</mat-icon>
                    <p>No transactions found in this batch</p>
                    <span>Try adjusting the filters above</span>
                </div>

                <!-- Transactions table: LMC .flat-table with expandable status-tracking rows -->
                <table mat-table [dataSource]="records" multiTemplateDataRows
                       class="flat-table records-table" *ngIf="!isLoading && records.length > 0">

                    <!-- Select Column -->
                    <ng-container matColumnDef="select">
                        <th mat-header-cell *matHeaderCellDef class="col-select">
                            <mat-checkbox [checked]="isAllSelected()"
                                          [indeterminate]="isSomeSelected()"
                                          [disabled]="selectMode === null || selectableRows.length === 0"
                                          [class.select-inactive]="selectMode === null"
                                          (change)="toggleAllSelectable()"
                                          [matTooltip]="getSelectAllTooltip()">
                            </mat-checkbox>
                        </th>
                        <td mat-cell *matCellDef="let row" class="col-select">
                            <span [matTooltip]="getSelectDisabledReason(row)" [matTooltipDisabled]="canSelectRow(row)">
                                <mat-checkbox [checked]="selected.has(row.id)"
                                              [disabled]="!canSelectRow(row)"
                                              [class.select-inactive]="selectMode === null"
                                              (change)="toggleRow(row)"
                                              (click)="$event.stopPropagation()">
                                </mat-checkbox>
                            </span>
                        </td>
                    </ng-container>

                    <!-- Status Column -->
                    <ng-container matColumnDef="status">
                        <th mat-header-cell *matHeaderCellDef>Status</th>
                        <td mat-cell *matCellDef="let row">
                            <span class="status-badge" [style.--status-color]="getStatusColor(row.status)">{{ row.status }}</span>
                        </td>
                    </ng-container>

                    <!-- Context Column: KapCharge return reason (Returned) or error message (failures) -->
                    <ng-container matColumnDef="context">
                        <th mat-header-cell *matHeaderCellDef>Context</th>
                        <td mat-cell *matCellDef="let row">
                            <!-- Returned: show the KapCharge return code and its reason -->
                            <span class="return-label"
                                  *ngIf="row.status === KapchargeTransactionStatus.Returned && row.returnCode; else contextError">
                                [{{ row.returnCode }}] {{ getResponseCodeLabel(row.returnCode) || 'Unknown return code' }}
                            </span>
                            <!-- Otherwise surface the error message (e.g. Error); truncate, full text on hover -->
                            <ng-template #contextError>
                                <span class="error-text"
                                      *ngIf="row.error; else noContext"
                                      [matTooltip]="row.error"
                                      matTooltipPosition="above"
                                      matTooltipClass="reason-tooltip">
                                    {{ row.error }}
                                </span>
                            </ng-template>
                            <ng-template #noContext>—</ng-template>
                        </td>
                    </ng-container>

                    <!-- Flow Column -->
                    <ng-container matColumnDef="flow">
                        <th mat-header-cell *matHeaderCellDef>Flow</th>
                        <td mat-cell *matCellDef="let row">
                            <span class="flow-badge" [ngClass]="getFlowClass(row.transactionFlow)">{{ row.transactionFlow }}</span>
                        </td>
                    </ng-container>

                    <!-- Loan # Column -->
                    <ng-container matColumnDef="loanNumber">
                        <th mat-header-cell *matHeaderCellDef>Loan #</th>
                        <td mat-cell *matCellDef="let row">{{ row.loanNumber || '—' }}</td>
                    </ng-container>

                    <!-- Customer Name Column -->
                    <ng-container matColumnDef="customerName">
                        <th mat-header-cell *matHeaderCellDef>Customer</th>
                        <td mat-cell *matCellDef="let row">{{ row.customerName || '—' }}</td>
                    </ng-container>

                    <!-- Banking Account Column -->
                    <ng-container matColumnDef="bankDetails">
                        <th mat-header-cell *matHeaderCellDef>Banking Account</th>
                        <td mat-cell *matCellDef="let row">
                            {{ row.institutionCode ? (row.institutionCode + '-' + row.transitNumber + ' / ' + row.accountNumber) : '—' }}
                        </td>
                    </ng-container>

                    <!-- Due Date Column -->
                    <ng-container matColumnDef="dueDate">
                        <th mat-header-cell *matHeaderCellDef>Due Date</th>
                        <td mat-cell *matCellDef="let row">
                            {{ row.dueDate ? FormatUtil.formatDate(row.dueDate, 'YYYY-MM-DD') : '—' }}
                        </td>
                    </ng-container>

                    <!-- Amount Column -->
                    <ng-container matColumnDef="amount">
                        <th mat-header-cell *matHeaderCellDef class="col-amount">Amount</th>
                        <td mat-cell *matCellDef="let row" class="col-amount">
                            {{ FormatUtil.formatCurrency(row.amountInCents / 100) }}
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef>Actions</th>
                        <td mat-cell *matCellDef="let row">
                            <button mat-icon-button *ngIf="isEditable(row)"
                                    (click)="onEditTransaction(row, $event)"
                                    matTooltip="Edit transaction">
                                <mat-icon>edit</mat-icon>
                            </button>
                        </td>
                    </ng-container>

                    <!-- Expanded Detail Row: transaction status tracking -->
                    <ng-container matColumnDef="expandedDetail">
                        <td mat-cell *matCellDef="let row" [attr.colspan]="displayedColumns.length" class="detail-cell">
                            <!-- Animated shell (overflow hidden) so the padded panel collapses to zero height -->
                            <div class="detail-content"
                                 [@detailExpand]="row === expandedRow ? 'expanded' : 'collapsed'">
                                <div class="detail-panel">

                                    <!-- Loading -->
                                    <div *ngIf="getDetail(row) === 'loading'" class="detail-loading">
                                        <mat-spinner diameter="24"></mat-spinner>
                                        <span>Loading details from Kapcharge...</span>
                                    </div>

                                    <!-- Error -->
                                    <div *ngIf="getDetail(row) === 'error'" class="detail-error">
                                        <mat-icon color="warn">error_outline</mat-icon>
                                        <span>Failed to load transaction details.</span>
                                    </div>

                                    <!-- Detail content -->
                                    <ng-container *ngIf="getDetail(row) as detail">
                                        <ng-container *ngIf="detail !== 'loading' && detail !== 'error'">
                                            <div class="detail-columns">

                                                <!-- Left: transaction summary -->
                                                <div class="detail-summary">
                                                    <p class="detail-section-title">Transaction Summary</p>
                                                    <div class="details-grid">
                                                        <div class="details-item">
                                                            <span class="details-label">Current Status</span>
                                                            <span class="details-value">
                                                                <span class="status-badge" [style.--status-color]="getStatusColor(row.status)">{{ row.status }}</span>
                                                            </span>
                                                        </div>
                                                        <div class="details-item" *ngIf="$any(detail).availableDate">
                                                            <span class="details-label">Available Date</span>
                                                            <span class="details-value">{{ FormatUtil.formatDate($any(detail).availableDate, 'YYYY-MM-DD') }}</span>
                                                        </div>
                                                        <div class="details-item" *ngIf="$any(detail).responseCode">
                                                            <span class="details-label">Return Reason</span>
                                                            <span class="details-value">
                                                                {{ $any(detail).responseCode }} — {{ getResponseCodeLabel($any(detail).responseCode) }}
                                                            </span>
                                                        </div>
                                                        <div class="details-item" *ngIf="$any(detail).createdDateTime">
                                                            <span class="details-label">Kapcharge Created At</span>
                                                            <span class="details-value">{{ FormatUtil.formatDate($any(detail).createdDateTime, 'YYYY-MM-DD HH:mm') }}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Right: status tracking timeline -->
                                                <div class="detail-tracking">
                                                    <p class="detail-section-title">Status Tracking</p>
                                                    <div class="event-timeline" *ngIf="getSortedEvents(row).length > 0; else noEvents">
                                                        <div class="timeline-item"
                                                             *ngFor="let event of getSortedEvents(row); let last = last">
                                                            <div class="timeline-marker" [ngClass]="getEventMarkerClass(event.eventStatus)">
                                                                <mat-icon class="marker-icon">{{ getEventIcon(event.eventStatus) }}</mat-icon>
                                                                <div class="timeline-line" *ngIf="!last"></div>
                                                            </div>
                                                            <div class="timeline-content">
                                                                <span class="event-type">{{ event.eventType }}</span>
                                                                <span class="event-description" *ngIf="event.description">{{ event.description }}</span>
                                                                <span class="event-date">{{ FormatUtil.formatDate(event.eventDate, 'YYYY-MM-DD HH:mm:ss') }}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ng-template #noEvents>
                                                        <p class="timeline-empty">No tracking events recorded yet.</p>
                                                    </ng-template>
                                                </div>

                                            </div>
                                        </ng-container>
                                    </ng-container>

                                </div>
                            </div>
                        </td>
                    </ng-container>

                    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
                    <tr mat-row *matRowDef="let row; columns: displayedColumns;"
                        [class.row-expandable]="isExpandable(row)"
                        [class.row-expanded]="row === expandedRow"
                        (click)="onRowClick(row)"
                        (keydown.enter)="onRowClick(row)"></tr>
                    <tr mat-row *matRowDef="let _row; columns: ['expandedDetail']" class="detail-row"></tr>

                </table>

                <mat-paginator
                    [length]="totalRows"
                    [pageSize]="pageSize"
                    [pageSizeOptions]="[25, 50, 100]"
                    [pageIndex]="currentPage"
                    showFirstLastButtons
                    (page)="onPageChange($event)">
                </mat-paginator>

            </mat-card-content>
        </mat-card>

    </ng-container>

</div>

<!-- Floating selection bar: shown while selectMode is on. Fixed to the viewport, not the
     page — the page scrolls now, and the bar has to stay reachable while it does. -->
<div class="selection-action-bar" *ngIf="selectMode !== null">
    <span class="selection-count">{{ selected.size }} selected</span>
    <div class="selection-actions">
        <button mat-stroked-button type="button" [disabled]="refunding || exporting || retrying || cancelling" (click)="exitSelectMode()">
            Close
        </button>
        <button *ngIf="selectMode === 'retry'" mat-raised-button color="primary" type="button"
                [disabled]="selected.size === 0 || retrying"
                (click)="onRetrySelected()">
            <mat-spinner *ngIf="retrying" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!retrying">refresh</mat-icon>
            {{ retrying ? 'Retrying…' : 'Retry (' + selected.size + ')' }}
        </button>
        <button *ngIf="selectMode === 'refund'" mat-raised-button color="primary" type="button"
                [disabled]="selected.size === 0 || refunding"
                (click)="onRefundSelected()">
            <mat-spinner *ngIf="refunding" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!refunding">currency_exchange</mat-icon>
            {{ refunding ? 'Refunding…' : 'Refund (' + selected.size + ')' }}
        </button>
        <button *ngIf="selectMode === 'export'" mat-raised-button color="primary" type="button"
                [disabled]="selected.size === 0 || exporting"
                (click)="onExportSelected()">
            <mat-spinner *ngIf="exporting" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!exporting">file_download</mat-icon>
            {{ exporting ? 'Exporting…' : 'Export (' + selected.size + ')' }}
        </button>
        <button *ngIf="selectMode === 'cancel'" mat-raised-button color="primary" type="button"
                [disabled]="selected.size === 0 || cancelling"
                (click)="onCancelSelected()">
            <mat-spinner *ngIf="cancelling" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!cancelling">block</mat-icon>
            {{ cancelling ? 'Cancelling…' : 'Cancel (' + selected.size + ')' }}
        </button>
    </div>
</div>
